<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <!--table hauptkonfigurationen -->
                <v-infinite-scroll @load="load">
                    <v-data-table-server
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="lieferantenDokumentList"
                        :loading="loading"
                        item-value="dokumentID"
                        :items-length="totalNumberOfRecords || 0"
                        hide-default-footer
                        class="elevation-1"
                        :must-sort="true"
                        hover
                        @update:sort-by="onSortByChanged"
                    >
                        <!-- Header der Table(Filter anzeige, gefundene Elemente) -->
                        <template #top>
                            <TableHeader :filters="filters" @chip-closed="removeFilter">
                                <template #default>
                                    <div class="table-header-item-count">
                                        {{ totalNumberOfRecords + ' ' + $t('lieferantendokument.archiv.table.header.items') }}
                                    </div>
                                    <v-text-field
                                        v-if="showSearchBar"
                                        v-model="filterData.suchText"
                                        class="ml-5"
                                        prepend-inner-icon="mdi-magnify"
                                        append-icon="mdi-close"
                                        :label="$t('lieferantendokument.archiv.table.header.searchlabel')"
                                        hide-details
                                        autofocus
                                        @update:model-value="searchFieldInputChanged"
                                        @click:append="searchBarClosed"
                                    ></v-text-field>
                                    <v-spacer />
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary">
                                                <v-icon v-if="!showSearchBar" @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('lieferantendokument.tableheader.tooltip.search') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary" @click="showFilterDrawer = !showFilterDrawer">
                                                <v-icon>mdi-filter-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.filter') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                </template>
                            </TableHeader>
                        </template>
                        <!-- Selbst definierte Tablespalten -->
                        <template #item.titel="{ item }">
                            <TextHighlights :text="item.titel" :search-text="filterData.suchText" />
                        </template>
                        <template #item.bezeichnung="{ item }">
                            <a style="text-decoration: underline">
                                <TextHighlights
                                    :text="item.bezeichnung"
                                    :search-text="filterData.suchText"
                                    @click="
                                        openDokument({
                                            id: item.dokumentGuid,
                                            dateiTyp: item.mimeTyp,
                                            dokumentName: item.bezeichnung
                                        })
                                    "
                                />
                            </a>
                        </template>
                        <template #item.teilnehmerId="{ item }">
                            <TextHighlights :text="item.firma" :search-text="filterData.suchText" />
                        </template>
                        <template #item.dokumentKategorieCode="{ item }">
                            <span v-if="item.dokumentKategorieCode == 'Vertrag'">{{ $t('lieferanten.dokument.dokumenkategorie.vertrag') }}</span>
                            <span v-else-if="item.dokumentKategorieCode == 'Lieferanteninformation'">{{
                                $t('lieferanten.dokument.dokumenkategorie.lieferanteninformation')
                            }}</span>
                            <span v-else-if="item.dokumentKategorieCode == 'Public'">{{ $t('lieferanten.dokument.dokumenkategorie.public') }}</span>
                            <span v-else-if="item.dokumentKategorieCode == 'Individuell'">{{ $t('lieferanten.dokument.dokumenkategorie.individuell') }}</span>
                        </template>
                        <template #item.link="{ item }">
                            <v-btn
                                icon
                                class="elevation-0"
                                :href="item.link"
                                @click="
                                    downloadDokument({
                                        id: item.dokumentGuid,
                                        dokumentName: item.bezeichnung
                                    })
                                "
                            >
                                <v-icon color="primary" icon="mdi-download-outline" />
                            </v-btn>
                        </template>
                        <template #item.burgermenu="{ item }">
                            <v-menu v-model="item.burgermenuActive" offset-x>
                                <template #activator="{ props }">
                                    <v-btn icon :href="item.burgermenu" v-bind="props" class="elevation-0">
                                        <v-icon color="primary" icon="mdi-menu" />
                                    </v-btn>
                                </template>
                                <v-list>
                                    <ConfirmDialog
                                        title="lieferantendokument.archiv.deletedialog.title"
                                        text="lieferantendokument.archiv.deletedialog.text"
                                        @confirm="deleteDokumentWrapper(item.dokumentId)"
                                    >
                                        <template #activatorElement="{ props }">
                                            <v-list-item v-bind="props">
                                                <v-list-item-title v-text="$t('archiv.table.burgermenu.delete')" />
                                            </v-list-item>
                                        </template>
                                    </ConfirmDialog>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-col>
        </v-row>
        <!-- FilterMenü -->
        <FilterDrawer
            v-model:visible="showFilterDrawer"
            @cancel="cancelFilter()"
            @confirm="confirmFilter()"
            @clear="clearFilters(true)"
            @reset="clearFilters(false)"
        >
            <template #default>
                <SingleFilter :title="$t('lieferantendokument.archiv.table.header.dokumentKategorie')">
                    <template #default>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="filterData.dokumentKategorieCode"
                                    variant="filled"
                                    density="compact"
                                    hide-details
                                    :items="dokumentKategorien"
                                ></v-select>
                            </v-col>
                        </v-row>
                    </template>
                </SingleFilter>
                <SingleFilter title="" class="py-0 mt-0">
                    <template #default>
                        <v-radio-group v-model="radios" class="pb-0 filterLine" inline>
                            <h5 class="pb-3">{{ $t('archiv.filter.title.datum') }}</h5>
                            <v-col cols="12" class="pt-0">
                                <v-radio value="selectTime" :label="$t('archiv.filter.datum.radio.selecttime')"></v-radio>
                            </v-col>
                            <v-col cols="12" class="pt-0">
                                <v-radio value="allTime" :label="$t('archiv.filter.datum.radio.alltime')"></v-radio>
                            </v-col>
                            <v-col cols="12" class="pt-0">
                                <v-radio value="specificTime" :label="$t('archiv.filter.datum.radio.specifictime')"></v-radio>
                            </v-col>
                            <v-col cols="12" class="pb-0 pt-0">
                                <v-row>
                                    <v-col class="pb-0" cols="6">
                                        <NowhowDatepicker v-model="filterData.dateFrom" :disabled="radios !== 'specificTime'" label="archiv.filter.datum.von">
                                        </NowhowDatepicker>
                                    </v-col>
                                    <v-col class="pb-0" cols="6">
                                        <NowhowDatepicker v-model="filterData.dateTo" :disabled="radios !== 'specificTime'" label="archiv.filter.datum.bis">
                                        </NowhowDatepicker>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-radio-group>
                    </template>
                </SingleFilter>
            </template>
        </FilterDrawer>
    </v-container>
</template>

<script lang="ts">
import { Component, Watch, mixins } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';
import { DateMixin } from '@/mixins/DateMixin';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
import { NAME as NAME_ADMINISTRATION } from '../store';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_RESOURCE } from '@/store/modules/resource';

const generalModul = namespace(NAME_GENERAL);
const administrationModul = namespace(NAME_ADMINISTRATION);
const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const resourceModul = namespace(NAME_RESOURCE);

import { GET_LANGUAGE } from '@/store/modules/resource/getters';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';
import { LOAD_LIEFERANTEN_DOKUMENTE, DELETE_DOKUMENT, OPEN_DOKUMENT, DOWNLOAD_DOKUMENT } from '../store/actions';
import { DOKUMENTE_LOADING, GET_LIEFERANTEN_DOKUMENTE, LIEFERANTEN_DOKUMENTE_TOTAL_NUMBER_OF_RECORDS } from '../store/getters';
import { CLEAR_LIEFERANTENDOKUMENT_FILTER, APPLY_LIEFERANTENDOKUMENT_FILTER } from '../store/mutations';
import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';

import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TableHeader from '@/components/TableHeader.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import NowhowDatepicker from '@/components/NowhowDatepicker.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import { TableHeaderFilterItem } from '@/types/TableHeaderFilterItem';
import { LieferantDokumentApiDto } from '@/api-schema';
import { LieferantenDokumentFilterDto } from '@/types/LieferantenDokumentFilterDto';
@Component({
    components: {
        SingleFilter,
        FilterDrawer,
        TableHeader,
        ConfirmDialog,
        NowhowDatepicker,
        TextHighlights
    }
})
export default class AdminLieferantDokumentArchiv extends mixins(DateMixin) {
    @administrationModul.Action(LOAD_LIEFERANTEN_DOKUMENTE) loadFilteredLieferantenDokumente!: Function;
    @administrationModul.Action(DOWNLOAD_DOKUMENT) downloadDokument!: Function;
    @administrationModul.Action(OPEN_DOKUMENT) openDokument!: Function;
    @administrationModul.Action(DELETE_DOKUMENT) deleteDokument!: Function;

    @administrationModul.Getter(GET_LIEFERANTEN_DOKUMENTE) lieferantenDokumentList!: Array<LieferantDokumentApiDto>;
    @administrationModul.Getter(DOKUMENTE_LOADING) loading!: boolean;
    @administrationModul.Getter(LIEFERANTEN_DOKUMENTE_TOTAL_NUMBER_OF_RECORDS) totalNumberOfRecords!: number;

    @administrationModul.Mutation(CLEAR_LIEFERANTENDOKUMENT_FILTER) clearFilter!: Function;
    @administrationModul.Mutation(APPLY_LIEFERANTENDOKUMENT_FILTER) applyFilters!: Function;

    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;
    @resourceModul.Getter(GET_LANGUAGE) language!: string;

    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    date = '';

    burgermenuActive = false;
    dialog = false;
    showSearchBar = false;
    searchField = '';
    showFilterDrawer = false;
    filters: Array<TableHeaderFilterItem> = [];
    filterData: LieferantenDokumentFilterDto = new LieferantenDokumentFilterDto();
    filterDataCopy: LieferantenDokumentFilterDto = new LieferantenDokumentFilterDto();
    timeFilter = 360;
    radios = 'selectTime';
    radiosCopy = '';
    sortBy: [{ key: 'createdOn'; order: 'desc' }];
    //Liste der selektierten Dokumente
    maximumFileSize = '-';
    infiniteLoad = null;

    get headers(): Array<any> {
        return [
            {
                title: this.$t('lieferantendokument.archiv.table.header.datum'),
                key: 'createdOn'
            },
            {
                title: this.$t('lieferantendokument.archiv.table.header.titel'),
                key: 'titel'
            },
            {
                title: this.$t('lieferantendokument.archiv.table.header.datei'),
                key: 'bezeichnung'
            },
            {
                title: this.$t('lieferantendokument.archiv.table.header.teilnehmerId'),
                key: 'teilnehmerId'
            },
            {
                title: this.$t('lieferantendokument.archiv.table.header.dokumentKategorie'),
                key: 'dokumentKategorieCode'
            },
            {
                title: this.$t('lieferantendokument.archiv.table.header.filetyp'),
                width: '95',
                key: 'dateiErweiterung'
            },
            {
                title: this.$t('lieferantendokument.archiv.table.header.download'),
                key: 'link',
                align: 'center',
                width: '10',
                sortable: false
            },
            {
                title: '',
                value: 'burgermenu',
                align: 'center',
                width: '10',
                sortable: false
            }
        ];
    }

    /* Funktion deklarieren, aber erst in mount mit debounce initialisieren */
    searchFieldInputChanged!: () => void;

    get dokumentKategorien(): Array<object> {
        return [
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.vertrag'),
                value: 'vertrag'
            },
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.lieferanteninformation'),
                value: 'lieferanteninformation'
            },
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.public'),
                value: 'public'
            },
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.individuell'),
                value: 'individuell'
            }
        ];
    }

    created() {
        this.setSubTitle(this.$t('lieferantendokument.archiv.title'));
        this.setFooterTitle('S940');
    }

    mounted() {
        if (!this.hasAccess('Navigation.AdminLieferantDokumentArchiv')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
            this.filterData.zeitspanne = 'Last365Days';
            this.filters.push({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: this.$t('archiv.filter.chip.datum.selecttime').toString()
            });
            this.applyFilters(this.filterData);
            this.loadFilteredLieferantenDokumente(0);
        }
        this.searchFieldInputChanged = debounce(() => {
            this.applyFilters(this.filterData);
            this.loadFilteredLieferantenDokumente(0);
        }, 400);
    }

    private clearFilters(closeFilter: boolean) {
        this.radios = 'selectTime';
        this.filterData = new LieferantenDokumentFilterDto();
        this.filterData.zeitspanne = 'Last365Days';
        if (closeFilter) {
            this.clearFilter();
            this.filters = [];
            this.applyFilters(this.filterData);
            this.showFilterDrawer = false;
        }
    }

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.suchText = undefined;
        this.filterData.dokumentKategorieCode = undefined;
        this.applyFilters(this.filterData);
        this.loadFilteredLieferantenDokumente(0);
    }

    private deleteDokumentWrapper(dokumentID: number) {
        this.deleteDokument(dokumentID);
    }

    private removeFilter(value: string) {
        this.filters = this.filters.filter(function (obj) {
            return obj.key !== value;
        });
        if (value === 'archiv.filter.chip.title.datum') {
            this.filterData.dateTo = undefined;
            this.filterData.dateFrom = undefined;
            this.filterData.zeitspanne = undefined;
            this.radios = 'allTime';
        } else if (value === 'lieferantendokument.archiv.table.header.dokumentKategorie') {
            this.filterData.dokumentKategorieCode = '';
        }
        this.applyFilters(this.filterData);
        this.loadFilteredLieferantenDokumente(0);
    }

    private ShowActiveFilters() {
        this.filters = [];
        if (this.radios === 'specificTime') {
            this.filterData.zeitspanne = undefined;
            let filterValue = '';
            if (this.filterData.dateFrom && this.filterData.dateTo) {
                filterValue = this.filterData.dateFrom + ' - ' + this.filterData.dateTo;
            } else if (this.filterData.dateFrom) {
                filterValue = '<= ' + this.filterData.dateFrom;
            } else if (this.filterData.dateTo) {
                filterValue = '>= ' + this.filterData.dateTo;
            }

            this.filters = this.filters.concat({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: filterValue
            });
        }
        if (this.radios === 'allTime') {
            this.filterData.dateTo = undefined;
            this.filterData.dateFrom = undefined;
            this.filterData.zeitspanne = undefined;
        }
        if (this.radios === 'selectTime') {
            this.filterData.dateTo = undefined;
            this.filterData.dateFrom = undefined;
            this.filterData.zeitspanne = 'Last365Days';
            this.filters = this.filters.concat({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: this.$t('archiv.filter.chip.datum.selecttime').toString()
            });
        }
        if (this.filterData.dokumentKategorieCode !== '') {
            this.filters = this.filters.concat({
                key: 'lieferantendokument.archiv.table.header.dokumentKategorie',
                nameResourceKey: 'lieferantendokument.archiv.table.header.dokumentKategorie',
                value: this.$t(this.filterData.dokumentKategorieCode).toString()
            });
        }
    }

    private confirmFilter() {
        this.ShowActiveFilters();
        this.applyFilters(this.filterData);
        this.loadFilteredLieferantenDokumente(0);
        this.showFilterDrawer = false;
    }

    private cancelFilter() {
        this.filterData = Object.assign({}, this.filterDataCopy);
        this.radios = this.radiosCopy;
        this.showFilterDrawer = false;
    }

    @Watch('showFilterDrawer')
    onFilterDrawerOpened() {
        if (this.showFilterDrawer) {
            this.filterDataCopy = Object.assign({}, this.filterData);
            this.radiosCopy = this.radios;
        }
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0]; // { key: 'lieferantName', order: 'desc' }

            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }
        this.applyFilters(this.filterData);
        this.loadFilteredLieferantenDokumente(0);
    }

    @Watch('language')
    onLanugageChanged() {
        this.ShowActiveFilters();
    }

    async load({ done }) {
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            // initialer datenload abwarten, sonst wird fälschlicherweise 'empty' emitted
            done('ok');
            return;
        }
        await this.loadFilteredLieferantenDokumente(this.lieferantenDokumentList.length);

        if (this.lieferantenDokumentList.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }

    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
